import { createAuthClient } from "better-auth/react"
import {
  convexClient,
  crossDomainClient,
} from "@convex-dev/better-auth/client/plugins"
import { emailOTPClient, usernameClient } from "better-auth/client/plugins"
import {
  EMPLOYER_EMAIL_UNVERIFIED_MESSAGE,
  EMPLOYER_PERSONAL_EMAIL_MESSAGE,
} from "../../convex/lib/workEmail"
import { errorMessage } from "@/lib/errors"
import { resetPostHogUser } from "@/lib/posthog"

export const authClient = createAuthClient({
  baseURL: import.meta.env.VITE_CONVEX_SITE_URL,
  plugins: [usernameClient(), emailOTPClient(), convexClient(), crossDomainClient()],
})

/** Employer work-email OTP: request a fresh code for the signed-in user. */
export async function sendEmployerVerificationOtp(email: string): Promise<boolean> {
  const res = await authClient.emailOtp.sendVerificationOtp({
    email,
    type: "email-verification",
  })
  return !res.error
}

export { EMPLOYER_EMAIL_UNVERIFIED_MESSAGE }

/**
 * better-auth reports a failed sign-out through the returned `error` object
 * rather than by throwing, so an unchecked `await authClient.signOut()` then
 * navigates home as if the session were gone. Returns whether it worked.
 */
export async function signOutSucceeded(): Promise<boolean> {
  try {
    const res = await authClient.signOut()
    if (res?.error) return false
    resetPostHogUser()
    return true
  } catch {
    return false
  }
}

export function employerPersonalEmailBlocked(profile: {
  role: string
  employerPersonalEmailBlocked?: boolean
}) {
  return profile.role === "employer" && profile.employerPersonalEmailBlocked === true
}

/**
 * Sign out when employer signup/login used a personal inbox. Matches on the
 * ConvexError payload, not `error.message`: production rewrites a server
 * error's message to "Server Error", which used to make this comparison fail
 * every time in prod and silently strand the user on the signup screen.
 */
export async function signOutIfEmployerPersonalEmail(error: unknown): Promise<boolean> {
  if (errorMessage(error, "") !== EMPLOYER_PERSONAL_EMAIL_MESSAGE) return false
  await signOutSucceeded()
  return true
}

export { EMPLOYER_PERSONAL_EMAIL_MESSAGE }

export const ROLE_STORAGE_KEY = "hsr_intended_role"
export const RETURN_TO_STORAGE_KEY = "hsr_return_to"

export type IntendedRole = "employer" | "seeker"

export function stashRole(role: IntendedRole) {
  sessionStorage.setItem(ROLE_STORAGE_KEY, role)
}

export function peekStashedRole(): IntendedRole | null {
  const role = sessionStorage.getItem(ROLE_STORAGE_KEY)
  if (role === "employer" || role === "seeker") return role
  return null
}

export function clearStashedRole() {
  sessionStorage.removeItem(ROLE_STORAGE_KEY)
}

export function takeStashedRole(): IntendedRole | null {
  const role = peekStashedRole()
  clearStashedRole()
  return role
}

/** Recruiter vs candidate from a path — used when Google login never stashed a role. */
export function roleFromPath(path: string | undefined | null): IntendedRole | null {
  if (!path || !path.startsWith("/") || path.startsWith("//")) return null
  const bare = path.split("?")[0] ?? path
  if (bare === "/recruit/join" || bare.startsWith("/recruit/join/")) return "employer"
  if (bare === "/jobs/join" || bare.startsWith("/jobs/join/")) return "seeker"
  if (bare.startsWith("/onboarding/company")) return "employer"
  if (bare.startsWith("/onboarding/candidate")) return "seeker"
  if (bare.startsWith("/candidate")) return "seeker"
  if (bare.startsWith("/recruit")) return "employer"
  return null
}

export function roleJoinPath(role: IntendedRole): "/jobs/join" | "/recruit/join" {
  return role === "employer" ? "/recruit/join" : "/jobs/join"
}

export function stashReturnTo(path: string | undefined) {
  if (!path) {
    sessionStorage.removeItem(RETURN_TO_STORAGE_KEY)
    return
  }
  sessionStorage.setItem(RETURN_TO_STORAGE_KEY, path)
}

export function takeReturnTo(): string | null {
  const path = sessionStorage.getItem(RETURN_TO_STORAGE_KEY)
  sessionStorage.removeItem(RETURN_TO_STORAGE_KEY)
  return path
}

/** Only allow same-origin relative paths for post-auth redirects. */
export function safeReturnPath(path: string | undefined | null): string | null {
  if (!path || !path.startsWith("/") || path.startsWith("//")) return null
  const bare = path.split("?")[0] ?? path
  // Never bounce back into auth/onboarding shells (causes /login?returnTo=/login).
  if (
    bare === "/login" ||
    bare === "/signup" ||
    bare.startsWith("/onboarding") ||
    bare === "/jobs/join" ||
    bare === "/recruit/join"
  ) {
    return null
  }
  return path
}

/**
 * Build a RoleGate/login returnTo that keeps search (invite tokens live in
 * `?token=`). `searchStr` is TanStack's `location.searchStr` (leading `?`).
 */
export function returnToFromLocation(pathname: string, searchStr: string): string {
  if (!searchStr || searchStr === "?") return pathname
  return `${pathname}${searchStr.startsWith("?") ? searchStr : `?${searchStr}`}`
}

/** Extract only a well-formed, same-site company invitation capability. */
export function companyInviteTokenFromPath(path: string | undefined | null) {
  if (!path || !path.startsWith("/") || path.startsWith("//")) return null
  const [pathname, query = ""] = path.split("?", 2)
  if (pathname !== "/recruit/invite") return null
  const token = new URLSearchParams(query).get("token")
  return token && /^[A-Za-z0-9_-]{32,128}$/.test(token) ? token : null
}

/**
 * Navigate to a safe return path without dropping query. Embedding `?token=`
 * in TanStack's `to` string loses search; invite tokens need an explicit
 * `search` object. Other return paths are bare route `to` values.
 */
export function navigateSafeReturn(
  navigate: (opts: { to: string; search?: { token: string } }) => unknown,
  path: string | undefined | null,
  fallback: string,
) {
  const dest = safeReturnPath(path) ?? fallback
  const inviteToken = companyInviteTokenFromPath(dest)
  if (inviteToken) {
    return navigate({ to: "/recruit/invite", search: { token: inviteToken } })
  }
  return navigate({ to: dest.split("?")[0] ?? dest })
}

export function roleHome(role: IntendedRole): "/recruit" | "/candidate" {
  return role === "employer" ? "/recruit" : "/candidate"
}

/** Per-role setup after signup — role is chosen at the join door, not here. */
export function roleSetupPath(
  role: IntendedRole,
): "/onboarding/company" | "/onboarding/candidate" {
  return role === "employer" ? "/onboarding/company" : "/onboarding/candidate"
}
